import { DiscoveryKeyword } from '@teko/discovery';
import {
  Body,
  ButtonIcon,
  Flex,
  LazyImage,
  Shape,
  SubTitle,
  Tooltip,
  styled,
} from '@teko/ds-desktop';
import { SearchOutlined, ShoppingCartOutlined } from '@teko/ds-icon';
import { getTrackingData } from '@teko/tracking';
import { Quantity } from 'components/Quantity';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import React, { useState } from 'react';

interface SearchListProps {
  suggestions: DiscoveryKeyword[];
  onSearch: (keywordObj: DiscoveryKeyword) => void;
  onHoverItem: (item: number) => void;
  selected: number | null;
  isSuggestProduct: boolean;
  onRedirectDetailProduct: (sku?: string) => void;
  onAddToCart: (item: DiscoveryKeyword, quantity?: number) => void;
}

const StyledSearchItem = styled(Flex)(
  (props: { selected: boolean; isSuggestProduct: boolean }) => `
  display: flex;
  padding: 0.6rem;
  cursor: pointer;
  border-radius: 8px;
  align-items: center;
  justify-content: ${props.isSuggestProduct ? 'space-between' : 'none'} ;
  background: ${props.selected ? '#f5f5f5' : '#fff'};
  border-bottom: 1px solid #f4f4f4;
`,
);

const StyledTextSuggestion = styled(SubTitle)`
  text-overflow: ellipsis;
`;

export const SearchList: React.FC<SearchListProps> = ({
  suggestions,
  onSearch,
  onHoverItem,
  selected,
  onAddToCart,
  onRedirectDetailProduct,
  isSuggestProduct = false,
}: SearchListProps) => {
  return (
    <>
      {suggestions.map((suggestion, index) => (
        <StyledSearchItem
          key={index}
          onClick={() =>
            !isSuggestProduct
              ? onSearch(suggestion)
              : onRedirectDetailProduct(suggestion.sku)
          }
          selected={index === selected}
          onMouseEnter={() => onHoverItem(index)}
          isSuggestProduct={isSuggestProduct}
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'searchSuggestion',
            contentName: suggestion.query,
            target: 'searchProductResult',
            payload: JSON.stringify({
              screenName: 'searchProduct',
              index,
            }),
          })}
        >
          {isSuggestProduct && (
            <ProductSuggestItem
              product={suggestion}
              onAddToCart={onAddToCart}
            />
          )}
          {!isSuggestProduct && !!suggestion?.query && (
            <>
              <SearchOutlined size={20} />
              <div style={{ margin: '0 0.6rem' }}>
                <SubTitle level={2} line={1} weight="regular">
                  {suggestion.query}
                </SubTitle>
              </div>
            </>
          )}
        </StyledSearchItem>
      ))}
    </>
  );
};

const ProductInfoWrapper = styled(Flex)`
  flex: 1;
  overflow: hidden;
`;
const ProductImageWrapper = styled(Shape)`
  min-width: 50px;
  width: 50px;
  position: relative;
`;
/** trust parent is flex */
const ProductSuggestItem = ({
  product,
  onAddToCart,
}: {
  product: DiscoveryKeyword;
  onAddToCart: SearchListProps['onAddToCart'];
}) => {
  const { imageUrl, highlight, uomName } = product;
  const [quantity, setQuantity] = useState(1);
  const { isDisplayUom } = useTenantConfig();
  return (
    <React.Fragment>
      <ProductInfoWrapper gapX="s16" align="center">
        <ProductImageWrapper>
          <LazyImage
            aspectRatio={1}
            contain
            src={imageUrl || ''}
            alt={highlight}
            borderRadius={5}
          />
        </ProductImageWrapper>
        <Shape>
          <Tooltip title={highlight || ''}>
            <StyledTextSuggestion level={2} line={1} weight="regular">
              {highlight}
            </StyledTextSuggestion>
          </Tooltip>
          {isDisplayUom && !!uomName ? (
            <Body color="textSecondary" level={1}>
              {t('Đơn vị tính:')} {uomName}
            </Body>
          ) : null}
        </Shape>
      </ProductInfoWrapper>
      <Flex gap="s16" align="center" gapX="s36" justify="space-between">
        <Quantity
          quantity={quantity}
          setQuantity={setQuantity}
          onClick={(e) => {
            e.stopPropagation && e.stopPropagation();
          }}
        />
        <Tooltip title={t('Thêm vào giỏ hàng')} placement="bottom">
          <ButtonIcon
            size={30}
            color="primary"
            onClick={(e) => {
              onAddToCart(product, quantity);
              e.stopPropagation && e.stopPropagation();
            }}
          >
            <ShoppingCartOutlined />
          </ButtonIcon>
        </Tooltip>
      </Flex>
    </React.Fragment>
  );
};
